/**
 * 🗿 **Model URLs that a browser will actually re-fetch when the model changes.**
 *
 * ## 🔴 WHY THIS FILE EXISTS
 *
 * **2026-09-23: a new sheep was built, shipped, deployed to staging AND dev —
 * and haugur looked at it and saw the old one.** 🎯 *"humm still seeing the old
 * weird necked sheep."*
 *
 * 🔬 **Nothing was wrong with the deploy, and measuring said so in one command.**
 * Both hosts served the new body — **3,985,400 bytes, a real `glTF` header,
 * byte-identical to what was committed.** ⚠️ **His browser simply never asked.**
 *
 * | | |
 * |---|---|
 * | 📛 **The name never changes** | A file in `public/` is copied verbatim, so it is `sheep.glb` for ever. 🔴 **Vite's content hashing — the thing that gives a changed asset a changed URL — does not apply to `public/`** |
 * | 🚫 **And nginx sends NO `Cache-Control` for `/models/`** | only a `Last-Modified` and an `ETag`. **With no instruction a browser decides for itself, and an open tab decides *no*** |
 *
 * 👉 ***A cache is not a bug until you change something. Then it is a bug that
 * looks exactly like the change never shipped*** — which is the most expensive
 * shape of fault this project keeps meeting, because it sends you to look in the
 * wrong place. ⚠️ **It cost a round trip with haugur to find**, and it would
 * have cost one on every future model.
 *
 * ## ⚠️ This is a CLIENT-side fix on purpose
 *
 * 🔴 **The server-side fix — a `Cache-Control` header on `/models/` — lives in
 * an nginx config that is NOT in this repo.** `06_Traps.md` calls that T90: a
 * setting that exists only on a host is a setting nobody can review and nobody
 * inherits. ✅ **A query string needs no host change at all**, so it cannot fall
 * out of step with a container.
 *
 * ⏭️ **The better long-term answer is to move the models out of `public/`** and
 * import them, so Vite content-hashes the filename and an UNCHANGED model is not
 * re-fetched. **That is a build change and it is its own ticket.**
 */

/**
 * 🗿 **Injected by `vite.config.ts`.**
 *
 * ⚠️ **In practice it is the BUILD TIME, not the commit sha.** Checked rather
 * than assumed: **nothing passes `LOFI_BUILD_SHA` into the client's Docker
 * build** — the server gets one at *runtime*, which is a different thing. The
 * config prefers the sha if it is ever set and falls back to `Date.now()`.
 * 👉 **Either way it changes on every build, which is the entire requirement.**
 *
 * ⚠️ **Declared here rather than in a `.d.ts`** so the constant and the reason
 * for it stay in one file.
 */
declare const __ASSET_STAMP__: string;

/**
 * 🗿 **Stamp a static asset URL so a new build is a new URL.**
 *
 * ⚠️ **Use it for anything under `public/` that can CHANGE between builds** —
 * models above all. A texture atlas packed into the bundle already gets a
 * content hash from Vite and does not need this.
 *
 * @example
 * export const SHEEP_URL = stamped('/models/sheep.glb');
 */
export function stamped(url: string): string {
  return `${url}${url.includes('?') ? '&' : '?'}v=${__ASSET_STAMP__}`;
}
